import React, { useState } from "react";
import { adminApi } from "../apiAdmin";
import { User } from "../types/admin";
import { AdminDeleteModal, AdminPasswordModal } from "./AdminAccountModals";
import AdminFaceIdCard from "./AdminFaceIdCard";
import AdminLoadFailed from "./AdminLoadFailed";

// Admins tab — create an admin, and the password / delete actions on the
// admin list. Moved out of pages/AdminDashboard.tsx in the Phase C split.

interface Props {
  admins: User[];
  setAdmins: React.Dispatch<React.SetStateAction<User[]>>;
  fetchAll: () => void;
  // The shell's /admins read failed: "Couldn't load admins" + Retry.
  adminsFailed: boolean;
}

// What the server says went wrong ({ msg }), or `fallback`.
function serverMessage(err: any, fallback: string): string {
  const msg = err?.response?.data?.msg;
  return typeof msg === "string" && msg ? msg : fallback;
}

export default function AdminAdminsTab({ admins, setAdmins, fetchAll, adminsFailed }: Props) {
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [passwordId, setPasswordId] = useState<string | null>(null);
  const [createAdmin, setCreateAdmin] = useState({ name: "", email: "", password: "" });
  const [createError, setCreateError] = useState("");

  const validateAdminPassword = (pw: string) =>
    pw.length >= 16 && /[A-Z]/.test(pw) && /[0-9]/.test(pw) && /[^A-Za-z0-9]/.test(pw);

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    const { name, email, password } = createAdmin;
    if (!validateAdminPassword(password)) {
      return setCreateError("Password must be ≥16 chars with uppercase, digit, and special char.");
    }
    try {
      // Server: POST /api/admin/create, owner (superadmin) only.
      await adminApi.post("/create", { name, email, password });
      setCreateAdmin({ name: "", email: "", password: "" });
      setCreateError("");
      fetchAll();
    } catch (err: any) {
      const forbidden = err?.response?.status === 403 && err?.response?.data?.msg !== "Invalid CSRF token";
      setCreateError(forbidden ? "Only the owner account can add admins." : serverMessage(err, "Error creating admin"));
    }
  };

  // The modal stays open on a failure, with the reason in an alert (the
  // admin tab's way); it closes only once the server took the new password.
  // A 404 with no { msg } is a route the live server doesn't have yet
  // (Express's default "Cannot PATCH …" page), not a missing admin.
  const handlePasswordChange = async (newPassword: string) => {
    try {
      await adminApi.patch(`/admins/${passwordId}/password`, { password: newPassword });
      setPasswordId(null);
    } catch (err: any) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.msg;
      if (status === 404 && !(typeof msg === "string" && msg)) alert("This needs the latest server update.");
      else if (status === 403 && msg !== "Invalid CSRF token") alert("Only the owner account can change admin passwords.");
      else alert(serverMessage(err, "Couldn't change the password. Try again."));
    }
  };

  const confirmDelete = async () => {
    if (!confirmId) return;
    try {
      await adminApi.delete(`/admins/${confirmId}`);
      setAdmins((prev) => prev.filter((x) => x._id !== confirmId));
      setConfirmId(null);
    } catch (err: any) {
      alert(err.response?.data?.msg || "Error deleting");
    }
  };

  return (
    <>
      <section className="fade-in">
        <AdminFaceIdCard />
        <div className="adm-card adm-mb-20">
          <h2 className="adm-section-title adm-mb-14">
            Create Admin
          </h2>
          <form onSubmit={handleCreateAdmin} className="adm-create-form">
            <input
              required
              placeholder="Name"
              value={createAdmin.name}
              onChange={(e) => setCreateAdmin({ ...createAdmin, name: e.target.value })}
            />
            <input
              required
              type="email"
              placeholder="Email"
              value={createAdmin.email}
              onChange={(e) => setCreateAdmin({ ...createAdmin, email: e.target.value })}
            />
            <input
              required
              type="password"
              placeholder="Password (≥16 chars)"
              value={createAdmin.password}
              onChange={(e) => setCreateAdmin({ ...createAdmin, password: e.target.value })}
            />
            <button className="adm-btn-green" type="submit">
              Create
            </button>
          </form>
          {createError && <p className="error-text">{createError}</p>}
        </div>

        {adminsFailed && <AdminLoadFailed what="admins" onRetry={fetchAll} />}
        {adminsFailed && admins.length === 0 ? null : (
        <div className="adm-table-wrapper">
          <table className="adm-table-light">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {admins.map((a) => (
                <tr key={a._id}>
                  <td className="adm-semibold">{a.name}</td>
                  <td className="muted-text">{a.email}</td>
                  <td>
                    <div className="action-btn-group">
                      <button
                        className="adm-btn-sm adm-btn-blue-sm"
                        onClick={() => setPasswordId(a._id)}
                      >
                        Password
                      </button>
                      <button
                        className="adm-btn-sm adm-btn-red-sm"
                        onClick={() => setConfirmId(a._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </section>
      {confirmId && <AdminDeleteModal onCancel={() => setConfirmId(null)} onConfirm={confirmDelete} />}
      {passwordId && <AdminPasswordModal onCancel={() => setPasswordId(null)} onSave={handlePasswordChange} />}
    </>
  );
}
